/**
 * Arriving at `/<club>/manage/settings#past-due-reminders` from a help
 * article: scroll to the setting, open whatever collapsed section or tab
 * holds it, and highlight it briefly.
 *
 * React Router never scrolls to a `#hash` by itself, and most settings
 * render only after their data loads, so <HelpAnchorScroller /> (mounted
 * once inside the router in App.tsx) waits for the target to appear.
 *
 * A section that hides its content until opened calls
 * `useHelpAnchorReveal("advanced-settings", () => setOpen(true))`. It opens
 * when the URL's anchor is that section or anything inside it (per the
 * `parent` chain in helpAnchors.ts), on mount and on later navigation.
 */
import { useEffect, useRef } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { HELP_ANCHORS, helpAnchorChain, isHelpAnchorId } from "./helpAnchors";

export const HELP_ANCHOR_REVEAL_EVENT = "help-anchor:reveal";
export const HELP_ANCHOR_HIGHLIGHT_CLASS = "help-anchor-highlight";
export const HELP_ANCHOR_HIGHLIGHT_MS = 2500;
export const HELP_ANCHOR_WAIT_MS = 10000;
const POLL_MS = 100;
/** Longest wait for a found setting to stop moving before scrolling anyway. */
export const HELP_ANCHOR_SETTLE_MS = 1500;
/** One look after scrolling, for layout that lands late (images, a section finishing opening). */
const RECHECK_MS = 400;

const SAFE_ID = /^[A-Za-z0-9_-]+$/;

export interface HelpAnchorRevealDetail {
  id: string;
  chain: string[];
}

/** The anchor id in a `#hash`, or null when there is none worth chasing. */
export function anchorIdFromHash(hash: string | null | undefined): string | null {
  if (!hash || hash.length < 2) return null;
  let id = hash.startsWith("#") ? hash.slice(1) : hash;
  try {
    id = decodeURIComponent(id);
  } catch {
    return null;
  }
  return SAFE_ID.test(id) ? id : null;
}

/** The element an anchor id points at: `data-help-anchor` first, then a plain `id`. */
export function findHelpAnchorElement(id: string): HTMLElement | null {
  if (!SAFE_ID.test(id)) return null;
  return (
    document.querySelector<HTMLElement>(`[data-help-anchor="${id}"]`) ||
    document.getElementById(id)
  );
}

/** Briefly outline an element so the reader's eye lands on it. */
export function highlightHelpAnchor(el: HTMLElement, ms = HELP_ANCHOR_HIGHLIGHT_MS): () => void {
  el.classList.add(HELP_ANCHOR_HIGHLIGHT_CLASS);
  const timer = window.setTimeout(() => el.classList.remove(HELP_ANCHOR_HIGHLIGHT_CLASS), ms);
  return () => {
    window.clearTimeout(timer);
    el.classList.remove(HELP_ANCHOR_HIGHLIGHT_CLASS);
  };
}

/**
 * The element that actually scrolls around `el`: the nearest ancestor with
 * scrollable overflow and more content than it shows. In the signed-in app
 * that is the layout's content area, not the window. Null when only the page
 * itself scrolls.
 */
export function scrollContainerOf(el: HTMLElement): HTMLElement | null {
  let node = el.parentElement;
  while (node && node !== document.body && node !== document.documentElement) {
    const { overflowY } = window.getComputedStyle(node);
    if (/(auto|scroll|overlay)/.test(overflowY) && node.scrollHeight > node.clientHeight) return node;
    node = node.parentElement;
  }
  return null;
}

/**
 * Put `el` in the middle of whatever scrolls around it, at once. A smooth
 * `scrollIntoView` started while the settings page was still opening sections
 * and loading data was cancelled by those layout changes, so the setting was
 * highlighted but never scrolled to (2026-10-10).
 */
export function scrollHelpAnchorIntoView(el: HTMLElement): void {
  const container = scrollContainerOf(el);
  if (container) {
    const elRect = el.getBoundingClientRect();
    const boxRect = container.getBoundingClientRect();
    const target =
      container.scrollTop + (elRect.top - boxRect.top) - Math.max(0, (container.clientHeight - elRect.height) / 2);
    container.scrollTop = Math.max(0, Math.round(target));
    return;
  }
  if (typeof el.scrollIntoView === "function") el.scrollIntoView({ block: "center" });
}

function isInView(el: HTMLElement): boolean {
  const r = el.getBoundingClientRect();
  const container = scrollContainerOf(el);
  const box = container
    ? container.getBoundingClientRect()
    : { top: 0, bottom: window.innerHeight || document.documentElement.clientHeight };
  return r.top >= box.top && r.bottom <= box.bottom;
}

/** Ask every collapsed section on the chain to open. */
export function requestHelpAnchorReveal(id: string): void {
  const detail: HelpAnchorRevealDetail = { id, chain: helpAnchorChain(id) };
  window.dispatchEvent(new CustomEvent(HELP_ANCHOR_REVEAL_EVENT, { detail }));
}

/**
 * Open the sections that hold `id`, wait for it to render and for the page
 * around it to stop moving, scroll it into view and highlight it. Returns a
 * cancel function.
 */
export function revealHelpAnchor(
  id: string,
  { waitMs = HELP_ANCHOR_WAIT_MS }: { waitMs?: number } = {}
): () => void {
  let cancelled = false;
  let pollTimer: number | undefined;
  let clearHighlight: (() => void) | undefined;
  const startedAt = Date.now();

  requestHelpAnchorReveal(id);

  // The setting has rendered, but the sections around it may still be
  // opening and loading. Scroll once it stops moving (or after
  // HELP_ANCHOR_SETTLE_MS), then look once more in case layout landed late.
  const settle = (foundAt: number, lastTop: number | null) => {
    if (cancelled) return;
    const el = findHelpAnchorElement(id);
    if (!el) {
      pollTimer = window.setTimeout(attempt, POLL_MS);
      return;
    }
    const top = el.getBoundingClientRect().top;
    const still = lastTop !== null && Math.abs(top - lastTop) < 1;
    if (!still && Date.now() - foundAt < HELP_ANCHOR_SETTLE_MS) {
      pollTimer = window.setTimeout(() => settle(foundAt, top), POLL_MS);
      return;
    }
    scrollHelpAnchorIntoView(el);
    clearHighlight = highlightHelpAnchor(el);
    pollTimer = window.setTimeout(() => {
      if (!cancelled && el.isConnected && !isInView(el)) scrollHelpAnchorIntoView(el);
    }, RECHECK_MS);
  };

  const attempt = () => {
    if (cancelled) return;
    const el = findHelpAnchorElement(id);
    if (el) {
      settle(Date.now(), null);
      return;
    }
    if (Date.now() - startedAt >= waitMs) return;
    // A section that loaded its data after the first request may only now
    // be listening; ask again so it can open.
    requestHelpAnchorReveal(id);
    pollTimer = window.setTimeout(attempt, POLL_MS);
  };
  attempt();

  return () => {
    cancelled = true;
    if (pollTimer !== undefined) window.clearTimeout(pollTimer);
    clearHighlight?.();
  };
}

/**
 * Open a collapsed section or tab when the current anchor is it or inside it.
 * `ids` is the section's own anchor id (or several, for tab sets); `reveal`
 * receives the matching id.
 */
export function useHelpAnchorReveal(
  ids: string | readonly string[],
  reveal: (matchedId: string) => void
): void {
  const revealRef = useRef(reveal);
  revealRef.current = reveal;
  const key = Array.isArray(ids) ? ids.join("|") : (ids as string);

  useEffect(() => {
    const own = key.split("|");
    const match = (chain: string[]) => own.find((id) => chain.includes(id));

    const current = anchorIdFromHash(window.location.hash);
    if (current) {
      const hit = match(helpAnchorChain(current));
      if (hit) revealRef.current(hit);
    }

    const onReveal = (event: Event) => {
      const detail = (event as CustomEvent<HelpAnchorRevealDetail>).detail;
      if (!detail?.chain) return;
      const hit = match(detail.chain);
      if (hit) revealRef.current(hit);
    };
    window.addEventListener(HELP_ANCHOR_REVEAL_EVENT, onReveal);
    return () => window.removeEventListener(HELP_ANCHOR_REVEAL_EVENT, onReveal);
  }, [key]);
}

/** `/<club>/manage/settings/all` — the settings index, not a settings page. */
const SETTINGS_INDEX = /^\/([^/]+)\/manage\/settings\/all\/?$/;

/**
 * Where an anchor link should really land. The settings index
 * (`/manage/settings/all`) lists every settings page but renders none of
 * them, so `/all#past-due-reminders` forwards to the page that has it.
 * Returns null when the current path is already right.
 */
export function forwardedAnchorPath(pathname: string, id: string): string | null {
  const m = SETTINGS_INDEX.exec(pathname);
  if (!m || !isHelpAnchorId(id)) return null;
  return HELP_ANCHORS[id].route.replace(":clubid", m[1]);
}

/** Mount once inside the router. Renders nothing. */
export function HelpAnchorScroller(): null {
  const location = useLocation();
  const navigate = useNavigate();

  useEffect(() => {
    const id = anchorIdFromHash(location.hash);
    if (!id) return undefined;
    const forward = forwardedAnchorPath(location.pathname, id);
    if (forward) {
      navigate(`${forward}${location.search}#${id}`, { replace: true });
      return undefined;
    }
    return revealHelpAnchor(id);
    // location.key: following the same link twice should scroll again.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.pathname, location.hash, location.key]);

  return null;
}

export default HelpAnchorScroller;
